import React, { useEffect, useState } from 'react';
import { Linking, Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Button } from './Button';
import { ScoreRing } from './ScoreRing';
import { Bookmark, Check, CircleAlert, ExternalLink, X } from './icons';
import { PastilleEntreprise } from './OffreCarte';
import { AvantPreparation, avantPreparationNecessaire } from './AvantPreparation';
import { FenetrePleinEcran } from './FenetrePleinEcran';
import { PreparationDossier } from './PreparationDossier';
import { RelectureDossier } from './RelectureDossier';
import { dossierPourOffre } from '../services/offres';
import { alertesMineur } from '../utils/offresMineurs';
import { URL_ADZUNA, URL_LICENCE_FRANCE_TRAVAIL } from '../config/offres';
import { adresseOuvrable, ouvrirAnnonce } from '../utils/ouvrirAnnonce';
import { noterEvenement } from '../services/collecte';
import { t } from '../i18n';
import { colors, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import type { DossierFinal, Offre } from '../types/offres';
import { dateCourte, libelleRaison, sourceLibelle } from '../utils/offresAffichage';
import { ContenuLtr } from './ContenuLtr';
import { TEXTE_LTR } from '../i18n/sens';

export interface OffreDetailProps {
  offre: Offre | null;
  onFermer: () => void;
  onSauvegarder?: () => void;
  sauvegardee?: boolean;
  /** null : inconnu — aucune règle pour les moins de 18 ans ne peut s'appliquer. */
  age?: number | null;
  naissanceConnue?: boolean;
  onNaissance?: (iso: string) => void;
  /** Un dossier vient d'aboutir : le compteur de crédits doit se relire. */
  onDossierPret?: (dossier: DossierFinal) => void;
  /** Affiché dans la page (volet de droite sur ordinateur) et non dans une fenêtre par-dessus. */
  enligne?: boolean;
}

type Vue = { nom: 'detail' } | { nom: 'avant' } | { nom: 'preparation' } | { nom: 'relecture'; dossierId: string };

const RUBRIQUES_CONNUES = new Set([
  'nature_contrat', 'type_contrat', 'duree_travail', 'experience', 'formations', 'competences', 'qualites',
  'savoirs', 'langues', 'permis', 'salaire', 'secteur', 'qualification', 'deplacements', 'conditions', 'postes',
  'accessible_th', 'contact', 'agence', 'duree_contrat', 'debut', 'expiration', 'rome',
]);

// Une rubrique ajoutée par une source avant d'être traduite reste affichée : la licence demande
// l'offre complète, pas l'offre moins ce que l'application ne sait pas encore nommer.
function libelleRubrique(cle: string): string {
  return RUBRIQUES_CONNUES.has(cle) ? t(`offres.detail.rubriques.${cle}`) : t('offres.detail.infos');
}

function ouvrir(url: string) {
  void Linking.openURL(url).catch(() => undefined);
}

/** Panneau de détail, ouvert depuis le bas : par le swipe à droite, ou par « Voir le détail ». */
export function OffreDetail({
  offre,
  onFermer,
  onSauvegarder,
  sauvegardee = false,
  age = null,
  naissanceConnue = true,
  onNaissance,
  onDossierPret,
  enligne = false,
}: OffreDetailProps) {
  const [vue, setVue] = useState<Vue>({ nom: 'detail' });
  const [dossierPret, setDossierPret] = useState<string | null>(null);
  // Une autre offre ouverte repart du détail, jamais d'une préparation restée affichée.
  useEffect(() => {
    setVue({ nom: 'detail' });
    setDossierPret(null);
    if (!offre) return;
    let actif = true;
    void dossierPourOffre(offre.cle).then((d) => {
      if (actif && d?.statut === 'pret') setDossierPret(d.id);
    });
    return () => {
      actif = false;
    };
  }, [offre?.cle]);
  if (!offre) return null;
  const alertes = alertesMineur(offre, age);
  const preparer = () => setVue(avantPreparationNecessaire(offre.score, naissanceConnue) ? { nom: 'avant' } : { nom: 'preparation' });
  const retiree = offre.retiree === true;
  // Adzuna ne transmet qu'un extrait : il est présenté comme tel, et mène à l'annonce complète.
  const adzuna = offre.source === 'adzuna';
  const meta = [offre.entreprise.nom, offre.lieu.libelle, t(`offres.contrats.${offre.contrat.code}`)].filter(Boolean).join(' · ');
  const maj = dateCourte(offre.misAJourLe ?? offre.publieeLe);

  const enveloppe = (contenu: React.ReactNode) =>
    enligne ? (
      <>{contenu}</>
    ) : (
      <Modal visible transparent animationType="slide" onRequestClose={onFermer}>
        <Pressable style={styles.fond} onPress={onFermer} accessible={false} />
        {contenu}
      </Modal>
    );

  return enveloppe(
    <>
      <View style={[styles.panneau, enligne && styles.panneauEnligne]} testID="offre-detail">
        <View style={styles.barre}>
          {enligne ? <View /> : (<Pressable
            accessibilityRole="button"
            accessibilityLabel={t('offres.detail.fermer')}
            onPress={onFermer}
            style={styles.rond}
          >
            <X size={22} color={colors.night} />
          </Pressable>)}
          {onSauvegarder && !retiree ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={sauvegardee ? t('offres.detail.sauvegardee') : t('offres.actions.sauvegarder')}
              accessibilityState={{ disabled: sauvegardee }}
              disabled={sauvegardee}
              onPress={onSauvegarder}
              style={styles.rond}
            >
              <Bookmark size={22} color={colors.night} fill={sauvegardee ? colors.night : 'none'} />
            </Pressable>
          ) : null}
        </View>

        <ScrollView contentContainerStyle={styles.contenu} showsVerticalScrollIndicator={false}>
          <View style={styles.entete}>
            <PastilleEntreprise offre={offre} taille={56} />
            <View style={styles.titres}>
              <ContenuLtr>
                <Text style={[styles.titre, TEXTE_LTR]}>{offre.titre}</Text>
              </ContenuLtr>
              {meta ? <Text style={styles.meta}>{meta}</Text> : null}
            </View>
          </View>

          {/* Une alerte, jamais un filtre : France Travail ne dit pas ce qui est interdit aux mineurs. */}
          {!retiree && alertes.length > 0 ? (
            <View style={styles.alerte} testID="alerte-mineur">
              <CircleAlert size={20} color={colors.corail} />
              <Text style={styles.alerteTexte}>
                {t('offres.detail.alerteMineur', { motifs: alertes.map((a) => t(`offres.detail.motifsMineur.${a}`)).join(', ') })}
              </Text>
            </View>
          ) : null}

          {retiree ? (
            <View style={styles.alerte}>
              <CircleAlert size={20} color={colors.bluePrimary} />
              <Text style={styles.alerteTexte}>{t('offres.detail.retiree')}</Text>
            </View>
          ) : (
            <>
              <View style={styles.bloc}>
                <View style={styles.matchEntete}>
                  <Text style={styles.blocTitre}>{t('offres.detail.monMatch')}</Text>
                  <View accessible accessibilityLabel={t('offres.carte.score', { score: offre.score })}>
                    <ScoreRing score={offre.score} size={56} />
                  </View>
                </View>
                {offre.raisons.map((r) => (
                  <View key={r.code} style={styles.raison}>
                    {r.ok ? (
                      <Check size={18} color={colors.success} strokeWidth={3} />
                    ) : (
                      <CircleAlert size={18} color={colors.bluePrimary} strokeWidth={2.5} />
                    )}
                    <Text style={styles.texte}>{libelleRaison(r)}</Text>
                  </View>
                ))}
              </View>

              {offre.description ? (
                <View style={styles.section}>
                  <Text style={styles.sectionTitre}>{t(adzuna ? 'offres.detail.extrait' : 'offres.detail.description')}</Text>
                  {adzuna ? <Text style={styles.petit}>{t('offres.detail.extraitAide')}</Text> : null}
                  <ContenuLtr>
                    <Text style={[styles.texte, TEXTE_LTR]}>{offre.description}</Text>
                  </ContenuLtr>
                </View>
              ) : null}

              {offre.details.length > 0 ? (
                <View style={styles.section}>
                  <Text style={styles.sectionTitre}>{t('offres.detail.infos')}</Text>
                  {offre.details.map((d, i) => (
                    <View key={`${d.cle}-${i}`} style={styles.ligne}>
                      <Text style={styles.ligneCle}>{libelleRubrique(d.cle)}</Text>
                      <ContenuLtr>
                        <Text style={[styles.texte, TEXTE_LTR]}>{d.valeur}</Text>
                      </ContenuLtr>
                    </View>
                  ))}
                </View>
              ) : null}

              {offre.entreprise.nom || offre.entreprise.description ? (
                <View style={styles.section}>
                  <Text style={styles.sectionTitre}>{t('offres.detail.entreprise')}</Text>
                  {offre.entreprise.nom ? <Text style={styles.ligneCle}>{offre.entreprise.nom}</Text> : null}
                  {offre.entreprise.description ? (
                    <ContenuLtr>
                      <Text style={[styles.texte, TEXTE_LTR]}>{offre.entreprise.description}</Text>
                    </ContenuLtr>
                  ) : null}
                </View>
              ) : null}
            </>
          )}

          <View style={styles.attribution}>
            {adzuna ? (
              // Conditions Adzuna : la mention, liée à leur site, d'au moins 116 × 23 px.
              <Pressable accessibilityRole="link" onPress={() => ouvrir(URL_ADZUNA)} style={[styles.lien, styles.lienAdzuna]}>
                <Text style={styles.lienTexte}>{t('offres.sources.adzuna')}</Text>
              </Pressable>
            ) : (
              <Text style={styles.petit}>{t('offres.detail.source', { source: sourceLibelle(offre.source, offre.partenaire) })}</Text>
            )}
            {maj ? <Text style={styles.petit}>{t('offres.detail.majLe', { date: maj })}</Text> : null}
            {/* Art. 4 : le lien de licence suit le CONTENU France Travail, y compris quand La bonne
                alternance le relaie (`partenaire`), et pas seulement la source qui l'a servi. */}
            {offre.source === 'france_travail' || offre.partenaire === 'France Travail' ? (
              <Pressable accessibilityRole="link" onPress={() => ouvrir(URL_LICENCE_FRANCE_TRAVAIL)} style={styles.lien}>
                <Text style={styles.lienTexte}>{t('offres.detail.licence')}</Text>
              </Pressable>
            ) : null}
          </View>
        </ScrollView>

        {retiree ? null : (
          <View style={styles.pied}>
            {offre.urlOrigine ? (
              <Pressable
                accessibilityRole="button"
                onPress={() => {
                  // Un clic vers l'annonce d'origine, jamais une candidature : rien ne dit qu'elle est déposée.
                  if (adresseOuvrable(offre.urlOrigine)) noterEvenement('offres_clic_annonce', { objetType: 'offre', objetId: offre.cle });
                  void ouvrirAnnonce(offre.urlOrigine as string);
                }}
                style={styles.origine}
              >
                <Text style={styles.origineTexte}>{t(adzuna ? 'offres.detail.voirOffreComplete' : 'offres.detail.voirOffre')}</Text>
                <ExternalLink size={18} color={colors.night} />
              </Pressable>
            ) : null}
            {dossierPret ? (
              <Button title={t('offres.detail.voirCandidature')} variant="secondary" onPress={() => setVue({ nom: 'relecture', dossierId: dossierPret })} />
            ) : null}
            <Button title={t('offres.detail.preparer')} variant="dark" onPress={preparer} />
          </View>
        )}
      </View>
      {/* Plein écran, dans UNE fenêtre ouverte par-dessus le détail et gardée pour tout le parcours :
          posés dans le panneau, ces écrans laissaient voir la page d'en dessous derrière l'heure
          (retour de test sur iPhone, 03/10/2026), et changer de fenêtre à chaque écran est la
          manipulation qui rend les modales fragiles sur iOS. */}
      {vue.nom !== 'detail' ? (
        <FenetrePleinEcran onFermer={() => setVue({ nom: 'detail' })}>
            {vue.nom === 'avant' ? (
              <AvantPreparation
                score={offre.score}
                naissanceConnue={naissanceConnue}
                onNaissance={(iso) => onNaissance?.(iso)}
                onContinuer={() => setVue({ nom: 'preparation' })}
                onAnnuler={() => setVue({ nom: 'detail' })}
              />
            ) : null}
            {vue.nom === 'preparation' ? (
              <PreparationDossier
                offre={offre}
                onFermer={() => setVue({ nom: 'detail' })}
                onRetourOffres={onFermer}
                onPret={(d) => {
                  setDossierPret(d.id);
                  onDossierPret?.(d);
                }}
                onVerifier={(id) => setVue({ nom: 'relecture', dossierId: id })}
              />
            ) : null}
            {vue.nom === 'relecture' ? <RelectureDossier dossierId={vue.dossierId} onFermer={() => setVue({ nom: 'detail' })} /> : null}
        </FenetrePleinEcran>
      ) : null}
    </>,
  );
}

const styles = StyleSheet.create({
  fond: { flex: 1, backgroundColor: colors.voile },
  panneau: {
    maxHeight: '92%',
    backgroundColor: colors.grey50,
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
  },
  panneauEnligne: {
    flex: 1,
    minHeight: 0,
    maxHeight: '100%',
    overflow: 'hidden',
    borderRadius: radius.xl,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  barre: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
  },
  rond: {
    minWidth: 44,
    minHeight: 44,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  contenu: { padding: screenPadding, gap: spacing.lg },
  entete: { flexDirection: 'row', gap: spacing.md, alignItems: 'flex-start' },
  titres: { flex: 1, gap: spacing.xs },
  titre: { fontSize: typography.h1.size, lineHeight: typography.h1.lineHeight, fontWeight: typography.h1.weight, color: colors.navy },
  meta: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  alerte: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'flex-start',
    padding: spacing.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.infoSoft,
  },
  alerteTexte: { flex: 1, fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.night },
  bloc: { gap: spacing.sm, padding: spacing.lg, borderRadius: radius.lg, backgroundColor: colors.white, ...shadow.card },
  matchEntete: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs },
  blocTitre: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.night },
  raison: { flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-start' },
  section: { gap: spacing.sm },
  sectionTitre: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.night },
  ligne: { gap: spacing.xs },
  ligneCle: { fontSize: typography.bodyMd.size, fontWeight: '600', color: colors.night },
  texte: { flex: 1, fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  attribution: { gap: spacing.xs, paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: colors.grey200 },
  petit: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  lien: { minHeight: 44, justifyContent: 'center' },
  lienAdzuna: { minWidth: 116, alignSelf: 'flex-start' },
  lienTexte: { fontSize: typography.small.size, color: colors.night, textDecorationLine: 'underline' },
  pied: {
    gap: spacing.sm,
    padding: screenPadding,
    paddingBottom: spacing.xxl,
    borderTopWidth: 1,
    borderTopColor: colors.grey200,
    backgroundColor: colors.white,
  },
  origine: {
    minHeight: 44,
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  origineTexte: { fontSize: typography.bodyMd.size, fontWeight: '600', color: colors.night, textDecorationLine: 'underline' },
});
